import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, Filter, Target, Calendar, Users2, Activity, Tag, 
  RotateCcw, Check, Boxes, ChevronRight, SlidersHorizontal 
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { MultiSelect } from '../../components/ui/MultiSelect';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';

export interface EngagementFilters {
  search: string;
  programIds: string[];
  programMatch: 'all' | 'any';
  statuses: string[];
  statusMatch: 'all' | 'any';
  audienceTypeIds: string[];
  audienceMatch: 'all' | 'any';
  categoryIds: string[];
  categoryMatch: 'all' | 'any';
  tags: string[];
  tagMatch: 'all' | 'any';
  startDate: string;
  endDate: string;
  noSessionsOnly: boolean;
}

interface EngagementFilterSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  filters: EngagementFilters;
  onChange: (filters: EngagementFilters) => void;
}

export const EngagementFilterSidebar: React.FC<EngagementFilterSidebarProps> = ({
  isOpen,
  onClose,
  filters,
  onChange,
}) => {
  // Lookups
  const { data: programs } = useQuery({
    queryKey: ['lookup-programs-filter'],
    queryFn: async () => {
      const { data } = await supabase.from('programs').select('id, name').order('name');
      return (data || []).map(p => ({ id: p.id, name: p.name }));
    }
  });

  const { data: categories } = useQuery({ 
    queryKey: ['lookup', 'engagement_categories'], 
    queryFn: async () => (await supabase.from('engagement_categories').select('id, label')).data || [] 
  });

  const { data: audienceTypes } = useQuery({ 
    queryKey: ['lookup', 'audience_types'], 
    queryFn: async () => (await supabase.from('audience_types').select('id, label')).data || [] 
  });

  const { data: tagRegistry } = useQuery({ 
    queryKey: ['lookup', 'engagement_tags_detailed'], 
    queryFn: async () => (await supabase.from('engagement_tags').select('id, label, group:group_id(label)')).data || [] 
  });

  const categoryOptions = useMemo(() => (categories || []).map(c => ({ id: c.id, name: c.label })), [categories]);
  const audienceOptions = useMemo(() => (audienceTypes || []).map(a => ({ id: a.id, name: a.label })), [audienceTypes]);
  const tagOptions = useMemo(() => (tagRegistry || []).map((t: any) => ({ 
    id: t.label, 
    name: `${t.group?.label ? `[${t.group.label}] ` : ''}${t.label}` 
  })), [tagRegistry]);

  const statusOptions = [
    { id: 'in development', name: 'In Development' },
    { id: 'in progress', name: 'In Progress' },
    { id: 'delivered', name: 'Delivered' },
    { id: 'on hold', name: 'On Hold' },
    { id: 'cancelled', name: 'Cancelled' }
  ];

  const handleReset = () => {
    onChange({
      search: filters.search,
      programIds: [],
      programMatch: 'any',
      statuses: [],
      statusMatch: 'any',
      audienceTypeIds: [],
      audienceMatch: 'any',
      categoryIds: [],
      categoryMatch: 'any',
      tags: [],
      tagMatch: 'any',
      startDate: '',
      endDate: '',
      noSessionsOnly: false
    });
  };

  const updateFilter = (patch: Partial<EngagementFilters>) => {
    onChange({ ...filters, ...patch });
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.programIds.length > 0) count++;
    if (filters.statuses.length > 0) count++;
    if (filters.audienceTypeIds.length > 0) count++;
    if (filters.categoryIds.length > 0) count++;
    if (filters.tags.length > 0) count++;
    if (filters.startDate) count++;
    if (filters.endDate) count++;
    if (filters.noSessionsOnly) count++;
    return count;
  }, [filters]);

  const LogicToggle = ({ 
    label, 
    value, 
    onChange: setLogic,
    isImpossible 
  }: { 
    label: string, 
    value: 'any' | 'all', 
    onChange: (v: 'any' | 'all') => void 
    isImpossible?: boolean
  }) => (
    <div className="flex items-center justify-between mb-2">
      <div className="flex flex-col">
        <h3 className="text-[11px] font-black text-slate-900 uppercase tracking-[0.2em]">{label}</h3>
        <p className="text-[9px] font-bold text-slate-400 uppercase mt-0.5">
          {value === 'any' ? `Match Any (OR)` : `Match All (AND)`}
        </p>
      </div>
      <div className="bg-slate-100 p-1 rounded-lg flex items-center shrink-0 ml-4">
        <button
          onClick={() => setLogic('any')}
          className={`px-3 py-1.5 rounded text-[9px] font-black uppercase tracking-widest transition-all ${value === 'any' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
        >
          Match Any
        </button>
        <button
          onClick={() => setLogic('all')}
          className={`px-3 py-1.5 rounded text-[9px] font-black uppercase tracking-widest transition-all ${value === 'all' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
        >
          Match All
        </button>
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100]"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-screen w-full max-w-md bg-white shadow-2xl z-[101] flex flex-col border-l border-slate-200"
          >
            <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-slate-900 flex items-center justify-center text-[#FFCC00] shadow-lg">
                  <SlidersHorizontal size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight">Filters</h2>
                  <div className="flex items-center gap-2">
                     <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                     <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{activeFiltersCount} Criteria Selected</p>
                  </div>
                </div>
              </div>
              <button 
                onClick={onClose} 
                className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-all font-bold"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-8 space-y-12 custom-scrollbar pb-32">
              {/* Programs */}
              <div className="space-y-3">
                <LogicToggle 
                  label="Programs" 
                  value={filters.programMatch} 
                  onChange={v => updateFilter({ programMatch: v })} 
                  isImpossible
                />
                <p className="text-[9px] font-bold text-slate-400 uppercase italic mb-2">
                  {filters.programMatch === 'all' ? 'Note: Matches results linked to ALL selected programs simultaneously (may return zero results).' : 'Matches results linked to ANY of the selected programs.'}
                </p>
                <MultiSelect
                  options={programs || []}
                  value={filters.programIds}
                  onChange={(ids) => updateFilter({ programIds: ids })}
                  placeholder="All Programs"
                />
              </div>

              {/* Status */}
              <div className="space-y-3">
                <LogicToggle 
                  label="Status" 
                  value={filters.statusMatch} 
                  onChange={v => updateFilter({ statusMatch: v })} 
                  isImpossible
                />
                <p className="text-[9px] font-bold text-slate-400 uppercase italic mb-2">
                  {filters.statusMatch === 'all' ? 'Note: A single record cannot have multiple statuses (may return zero results).' : 'Show results matching ANY of the selected statuses.'}
                </p>
                <MultiSelect
                  options={statusOptions}
                  value={filters.statuses}
                  onChange={(st) => updateFilter({ statuses: st })}
                  placeholder="All Statuses"
                />
              </div>

              {/* Audience Type */}
              <div className="space-y-3">
                <LogicToggle 
                  label="Audience Type" 
                  value={filters.audienceMatch} 
                  onChange={v => updateFilter({ audienceMatch: v })} 
                  isImpossible
                />
                <p className="text-[9px] font-bold text-slate-400 uppercase italic mb-2">
                  {filters.audienceMatch === 'all' ? 'Note: Items belong to exactly one audience type (may return zero results).' : 'Matches ANY selected audience types.'}
                </p>
                <MultiSelect
                  options={audienceOptions}
                  value={filters.audienceTypeIds}
                  onChange={(ids) => updateFilter({ audienceTypeIds: ids })}
                  placeholder="All Audience Types"
                />
              </div>

              {/* Structure */}
              <div className="space-y-3">
                <LogicToggle 
                  label="Structure" 
                  value={filters.categoryMatch} 
                  onChange={v => updateFilter({ categoryMatch: v })} 
                  isImpossible
                />
                <p className="text-[9px] font-bold text-slate-400 uppercase italic mb-2">
                   {filters.categoryMatch === 'all' ? 'Note: Items have exactly one structure (may return zero results).' : 'Matches ANY selected structures.'}
                </p>
                <MultiSelect
                  options={categoryOptions}
                  value={filters.categoryIds}
                  onChange={(ids) => updateFilter({ categoryIds: ids })}
                  placeholder="All Structures"
                />
              </div>

              {/* Tags */}
              <div className="space-y-3">
                <LogicToggle 
                  label="Tags" 
                  value={filters.tagMatch} 
                  onChange={v => updateFilter({ tagMatch: v })} 
                />
                <p className="text-[9px] font-bold text-slate-400 uppercase italic mb-2">
                   {filters.tagMatch === 'all' ? 'Show results that contain EVERY selected tag.' : 'Show results containing at least ONE of the selected tags.'}
                </p>
                <MultiSelect
                  options={tagOptions}
                  value={filters.tags}
                  onChange={(t) => updateFilter({ tags: t })}
                  placeholder="All Tags"
                />
              </div>

              {/* Date Range */}
              <div className="space-y-6 pt-6 border-t border-slate-100">
                <div className="flex items-center gap-3">
                  <Calendar size={16} className="text-slate-400" />
                  <h3 className="text-[11px] font-black text-slate-900 uppercase tracking-[0.2em]">Date Range</h3>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Earliest Start</label>
                    <input 
                      type="date" 
                      value={filters.startDate}
                      onChange={(e) => updateFilter({ startDate: e.target.value })}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded font-bold text-[10px] outline-none focus:ring-4 focus:ring-slate-100 uppercase" 
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1">Latest End</label>
                    <input 
                      type="date" 
                      value={filters.endDate}
                      onChange={(e) => updateFilter({ endDate: e.target.value })}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded font-bold text-[10px] outline-none focus:ring-4 focus:ring-slate-100 uppercase" 
                    />
                  </div>
                </div>
              </div>

              {/* Sessions Isolation */}
              <div className="pt-8 border-t border-slate-100">
                <label className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-100 rounded-xl cursor-pointer hover:bg-slate-100 transition-colors group">
                  <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${filters.noSessionsOnly ? 'bg-slate-900 border-slate-900 shadow-sm' : 'bg-white border-slate-200'}`}>
                    {filters.noSessionsOnly && <Check size={14} className="text-[#FFCC00]" />}
                  </div>
                  <input 
                    type="checkbox" 
                    className="hidden" 
                    checked={filters.noSessionsOnly}
                    onChange={(e) => updateFilter({ noSessionsOnly: e.target.checked })}
                  />
                  <div>
                    <span className="text-[11px] font-black text-slate-900 uppercase tracking-widest block">No Activities Recorded</span>
                    <span className="text-[9px] font-bold text-slate-400 uppercase">Show only engagements with zero sessions linked</span>
                  </div>
                </label>
              </div>
            </div>

            <div className="p-8 border-t border-slate-100 bg-white sticky bottom-0 flex gap-4">
              <Button 
                variant="outline" 
                className="flex-1 py-4 border-slate-200 text-slate-500 hover:bg-slate-50 uppercase tracking-widest font-black text-[10px]"
                onClick={handleReset}
                icon={<RotateCcw size={18} />}
              >
                Reset All
              </Button>
              <Button 
                className="flex-[2] py-4 shadow-xl shadow-[#FFCC00]/20 bg-[#FFCC00] hover:bg-[#E6B800] text-slate-900 border-none uppercase tracking-widest font-black text-[10px]"
                onClick={onClose}
                icon={<ChevronRight size={18} />}
              >
                Apply Filters
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};


